import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { navigate } from '@reach/router';
import moment from 'moment';
import 'moment-timezone';
import InvoiceDetailDialog from './InvoiceDetailDialog';
import ILocalAGGrid, {
  getDefaultColDef,
  getActionsColumn,
  actionMenuItem,
} from '../grid/iLocalAGGrid';

const styles = (theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow:
      '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.08)',
  },
  filterRowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  filterControls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableWrapper: {
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: '#dc2626',
    padding: theme.spacing(2),
  },
  invoiceNumber: {
    fontWeight: 600,
    color: '#1e293b',
  },
  accountName: {
    fontWeight: 500,
    color: '#334155',
  },
  amount: {
    fontWeight: 600,
    color: '#059669',
  },
});

const FILTER_OPTIONS = [
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom Date Range' },
];

const PICKER_PAGE_SIZE = 10;

function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function buildAccountOptions(uiAccounts) {
  const base = ensureArray(uiAccounts).map((account) => {
    const raw =
      (account && (account.accountId || account.id || account.name)) ||
      'account';
    const label =
      (account && account.name) ||
      (account && account.displayName) ||
      (account && account.accountName) ||
      raw;
    return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All accounts' }, ...base];
}

function buildLocationOptions(payload, accountId) {
  let locations = ensureArray(
    payload && payload.locationTraffic && payload.locationTraffic.locations
  );
  if (accountId && accountId !== 'all') {
    locations = locations.filter(
      (loc) => String(loc.accountId) === String(accountId)
    );
  }
  const base = locations.map((location) => {
    const raw =
      (location && (location.boxId || location.id || location.boxName)) ||
      'location';
    const label =
      (location && (location.boxName || location.name)) || String(raw);
    return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All locations' }, ...base];
}

const BillingPicker = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const ref = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      (options || []).map((option) => ({
        ...option,
        value: String(option.value),
        label: option.label || String(option.value),
      })),
    [options]
  );

  const selected = useMemo(
    () =>
      normalizedOptions.find((opt) => opt.value === String(value)) ||
      normalizedOptions[0] || { value: 'all', label: 'All' },
    [normalizedOptions, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return normalizedOptions;
    }
    const q = search.toLowerCase();
    return normalizedOptions.filter((opt) =>
      String(opt.label).toLowerCase().includes(q)
    );
  }, [normalizedOptions, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PICKER_PAGE_SIZE)
  );
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PICKER_PAGE_SIZE;
  const visibleOptions = filtered.slice(
    startIndex,
    startIndex + PICKER_PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [search, normalizedOptions]);

  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback(
    (nextValue) => {
      setOpen(false);
      if (onChange) {
        onChange(nextValue);
      }
    },
    [onChange]
  );

  const handlePageChange = (direction) => {
    setPage((prev) => {
      if (direction === 'prev') {
        return Math.max(1, prev - 1);
      }
      if (direction === 'next') {
        return Math.min(totalPages, prev + 1);
      }
      return prev;
    });
  };

  return (
    <div
      ref={ref}
      style={{ position: 'relative', minWidth: 200, maxWidth: 260 }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          border: '1px solid #d1d5db',
          background: '#fff',
          borderRadius: 6,
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.85rem',
          color: '#374151',
          cursor: 'pointer',
        }}
      >
        <span style={{ textAlign: 'left' }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              color: '#6b7280',
              marginBottom: 2,
            }}
          >
            {label}
          </div>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {selected && selected.label ? selected.label : 'Select'}
          </div>
        </span>
        <span
          className={`icon-arrow-${open ? 'up' : 'down'}2`}
          style={{ marginLeft: 6 }}
        />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            width: 280,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
            zIndex: 20,
            padding: 10,
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${String(label).toLowerCase()}...`}
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              padding: '6px 8px',
              fontSize: '0.85rem',
              marginBottom: 8,
            }}
          />
          <div
            style={{
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
            }}
          >
            {visibleOptions.length ? (
              visibleOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  role="button"
                  style={{
                    padding: '8px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    background:
                      opt.value === String(selected.value)
                        ? '#e0ebff'
                        : 'transparent',
                    color:
                      opt.value === String(selected.value)
                        ? '#1d4ed8'
                        : '#111827',
                    fontWeight:
                      opt.value === String(selected.value) ? 600 : 400,
                  }}
                >
                  <span>{opt.label}</span>
                  {opt.value === String(selected.value) && (
                    <span style={{ fontSize: '0.8rem', marginLeft: 8 }}>
                      ✔
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px 0',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                No results found.
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 8,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <div>
              Page {totalPages ? currentPage : 0} of {totalPages}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                style={{ marginRight: 4 }}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 10,
    useGrouping: true,
  }).format(num);
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value) || 0);
}

const InvoiceList = ({ classes }) => {
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const [filter, setFilter] = useState('current_month');
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [startDate, setStartDate] = useState(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    moment().endOf('month').format('YYYY-MM-DD')
  );
  const [accountId, setAccountId] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [accountOptions, setAccountOptions] = useState([
    { value: 'all', label: 'All accounts' },
  ]);
  const [locationOptions, setLocationOptions] = useState([
    { value: 'all', label: 'All locations' },
  ]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  const loadingDetailsRef = useRef(loadingDetails);
  const loadingDownloadRef = useRef(loadingDownload);
  loadingDetailsRef.current = loadingDetails;
  loadingDownloadRef.current = loadingDownload;

  useEffect(() => {
    let cancelled = false;
    async function fetchDirectory() {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.signInUserSession) {
          return;
        }
        const token = user.signInUserSession.idToken.jwtToken;
        const params = new URLSearchParams({
          rangeDays: '30',
          timezone,
        });
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/usage-metrics?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) {
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        const accounts = buildAccountOptions(body && body.uiAccounts);
        setAccountOptions(accounts);
        if (body && body.locationTraffic && body.locationTraffic.locations) {
          const locations = buildLocationOptions(body, accountId);
          setLocationOptions(locations);
          if (!locations.some((opt) => opt.value === locationId)) {
            setLocationId('all');
          }
        }
      } catch (err) {
        console.error('[InvoiceList] Error fetching directory:', err);
      }
    }
    fetchDirectory();
    return () => {
      cancelled = true;
    };
  }, [timezone, accountId, locationId]);

  useEffect(() => {
    let cancelled = false;
    async function updateLocations() {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.signInUserSession) {
          return;
        }
        const token = user.signInUserSession.idToken.jwtToken;
        const params = new URLSearchParams({
          rangeDays: '30',
          timezone,
        });
        if (accountId !== 'all') {
          params.append('accountIds', accountId);
        }
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/usage-metrics?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) {
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        if (body && body.locationTraffic && body.locationTraffic.locations) {
          const locations = buildLocationOptions(body, accountId);
          setLocationOptions(locations);
          if (!locations.some((opt) => opt.value === locationId)) {
            setLocationId('all');
          }
        }
      } catch (err) {
        console.error('[InvoiceList] Error updating locations:', err);
      }
    }
    updateLocations();
    return () => {
      cancelled = true;
    };
  }, [accountId, timezone, locationId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchInvoices() {
      setLoading(true);
      setError('');
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.signInUserSession) {
          throw new Error('User not authenticated');
        }

        const token = user.signInUserSession.idToken.jwtToken;
        const params = new URLSearchParams();

        if (filter === 'custom' && useCustomDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        } else {
          params.append('filter', filter);
        }

        if (accountId !== 'all') {
          params.append('accountId', accountId);
        }

        if (locationId !== 'all') {
          params.append('locationId', locationId);
        }

        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}api/ilocal-invoices/list?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Unauthorized. Please login again.');
          }
          if (res.status === 404) {
            throw new Error(
              'Invoice API endpoint not found. Please ensure the backend routes are configured at /api/ilocal-invoices/*'
            );
          }
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${res.status}`);
        }

        const body = await res.json();
        if (cancelled) return;

        if (body.success && body.data) {
          setInvoices(Array.isArray(body.data) ? body.data : []);
          if (body.dateRange) {
            setDateRange(body.dateRange);
          }
        } else {
          setInvoices([]);
        }
      } catch (err) {
        console.error('[InvoiceList] Error fetching invoices:', err);
        if (!cancelled) {
          setError(err.message || 'Unable to load invoices.');
          setInvoices([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchInvoices();
    return () => {
      cancelled = true;
    };
  }, [filter, useCustomDate, startDate, endDate, accountId, locationId]);

  const handleViewDetails = useCallback(async (invoice) => {
    setLoadingDetails(invoice.id);
    setError('');
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user || !user.signInUserSession) {
        throw new Error('User not authenticated');
      }

      const token = user.signInUserSession.idToken.jwtToken;
      const res = await fetch(
        `${process.env.REACT_APP_BASEURL}api/ilocal-invoices/${invoice.id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Unauthorized. Please login again.');
        }
        if (res.status === 403) {
          throw new Error('You do not have access to this invoice.');
        }
        if (res.status === 404) {
          throw new Error('Invoice not found or API endpoint not configured.');
        }
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${res.status}`);
      }

      const body = await res.json();
      if (body.success && body.data) {
        setSelectedInvoice(body.data);
        setDetailDialogOpen(true);
      }
    } catch (err) {
      console.error('[InvoiceList] Error fetching invoice details:', err);
      setError(err.message || 'Unable to load invoice details.');
    } finally {
      setLoadingDetails(null);
    }
  }, []);

  const handleDownload = useCallback(
    async (invoice) => {
      setLoadingDownload(invoice.id);
      setError('');
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.signInUserSession) {
          throw new Error('User not authenticated');
        }

        const token = user.signInUserSession.idToken.jwtToken;
        const params = new URLSearchParams();

        if (filter === 'custom' && useCustomDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        } else {
          params.append('filter', filter);
        }

        if (accountId !== 'all') {
          params.append('accountId', accountId);
        }

        if (locationId !== 'all') {
          params.append('locationId', locationId);
        }

        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}api/ilocal-invoices/download?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${res.status}`);
        }

        const body = await res.json();
        if (body.success && body.invoices) {
          const invoiceToDownload = body.invoices.find(
            (inv) => inv.invoiceNumber === invoice.invoiceNumber
          );

          if (invoiceToDownload && invoiceToDownload.s3Url) {
            window.open(invoiceToDownload.s3Url, '_blank');
          } else if (invoice.s3Url) {
            window.open(invoice.s3Url, '_blank');
          } else {
            setError('Invoice file not available for download yet.');
          }
        }
      } catch (err) {
        console.error('[InvoiceList] Error downloading invoice:', err);
        setError(err.message || 'Unable to download invoice.');
      } finally {
        setLoadingDownload(null);
      }
    },
    [filter, useCustomDate, startDate, endDate, accountId, locationId]
  );

  const openRowActionsMenu = useCallback(
    (event, params, menuHelpers) => {
      const row = params && params.data;
      if (!row) return;
      const busy =
        loadingDetailsRef.current === row.id ||
        loadingDownloadRef.current === row.id;
      const items = [
        actionMenuItem({
          name: 'View Details',
          icon: 'view',
          disabled: busy,
          action: () => handleViewDetails(row),
        }),
        actionMenuItem({
          name: 'Download',
          icon: 'download',
          disabled: busy,
          action: () => handleDownload(row),
        }),
      ];
      if (menuHelpers && typeof menuHelpers.openMenu === 'function') {
        menuHelpers.openMenu(items);
      }
    },
    [handleViewDetails, handleDownload]
  );

  const getContextMenuItems = useCallback(
    (params) => {
      const colId =
        params &&
        params.column &&
        (params.column.getColId
          ? params.column.getColId()
          : params.column.colId);
      if (colId !== 'actions') return [];
      const row = params && params.node && params.node.data;
      if (!row) return [];
      const busy =
        loadingDetailsRef.current === row.id ||
        loadingDownloadRef.current === row.id;
      return [
        actionMenuItem({
          name: 'View Details',
          icon: 'view',
          disabled: busy,
          action: () => handleViewDetails(row),
        }),
        actionMenuItem({
          name: 'Download',
          icon: 'download',
          disabled: busy,
          action: () => handleDownload(row),
        }),
      ];
    },
    [handleViewDetails, handleDownload]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'month',
        headerName: 'Month',
        minWidth: 140,
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice Number',
        minWidth: 160,
        cellRenderer: (params) => (
          <span className={classes.invoiceNumber}>{params.value || ''}</span>
        ),
      },
      {
        field: 'accountName',
        headerName: 'Account Name',
        minWidth: 180,
        flex: 1,
        valueGetter: (params) => {
          const d = params.data || {};
          return d.accountName || d.storeName || d.locationName || 'N/A';
        },
        cellRenderer: (params) => (
          <span className={classes.accountName}>{params.value || 'N/A'}</span>
        ),
      },
      {
        field: 'totalMessages',
        headerName: 'Total Messages',
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (p) => formatNumber(p.value),
      },
      {
        field: 'inboundMessages',
        headerName: 'Inbound Messages',
        minWidth: 140,
        type: 'rightAligned',
        valueFormatter: (p) => formatNumber(p.value),
      },
      {
        field: 'outboundMessages',
        headerName: 'Outbound Messages',
        minWidth: 150,
        type: 'rightAligned',
        valueFormatter: (p) => formatNumber(p.value),
      },
      {
        field: 'totalMonthlySpend',
        headerName: 'Total Spend',
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (p) => formatCurrency(p.value),
        cellRenderer: (params) => (
          <span className={classes.amount}>
            {formatCurrency(params.value)}
          </span>
        ),
      },
      getActionsColumn({
        headerName: 'Actions',
        onActionsClick: (event, params, menuHelpers) =>
          openRowActionsMenu(event, params, menuHelpers),
      }),
    ],
    [classes, openRowActionsMenu]
  );

  const getDisplayDateRange = () => {
    if (filter === 'custom' && useCustomDate) {
      return {
        start: moment(startDate).format('MMM D, YYYY'),
        end: moment(endDate).format('MMM D, YYYY'),
      };
    }

    if (dateRange && dateRange.start && dateRange.end) {
      return {
        start: moment(dateRange.start).format('MMM D, YYYY'),
        end: moment(dateRange.end).format('MMM D, YYYY'),
      };
    }

    const pacificTime = moment.tz('America/Los_Angeles');
    let start;
    let end;

    switch (filter) {
      case 'current_month':
        start = pacificTime.clone().startOf('month');
        end = pacificTime.clone().endOf('month');
        break;
      case 'last_3_months':
        start = pacificTime.clone().subtract(2, 'months').startOf('month');
        end = pacificTime.clone().endOf('month');
        break;
      case 'last_6_months':
        start = pacificTime.clone().subtract(5, 'months').startOf('month');
        end = pacificTime.clone().endOf('month');
        break;
      case 'last_12_months':
        start = pacificTime.clone().subtract(11, 'months').startOf('month');
        end = pacificTime.clone().endOf('month');
        break;
      default:
        return null;
    }

    return {
      start: start.format('MMM D, YYYY'),
      end: end.format('MMM D, YYYY'),
    };
  };

  const displayDateRange = getDisplayDateRange();

  return (
    <div className={classes.container}>
      <Typography variant="h5" gutterBottom>
        Invoices
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        View and manage your SMS/MMS invoices
      </Typography>

      <div className={classes.filterRow}>
        <div className={classes.filterRowTop}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/home/billing')}
          >
            Back to Billing
          </Button>
          {dateRange && !useCustomDate && (
            <Typography variant="caption" color="textSecondary">
              {moment(dateRange.start).format('MMM D, YYYY')} –{' '}
              {moment(dateRange.end).format('MMM D, YYYY')}
            </Typography>
          )}
        </div>
        <div className={classes.filterControls}>
          <TextField
            select
            size="small"
            label="Time Period"
            value={filter}
            onChange={(e) => {
              const newFilter = e.target.value;
              setFilter(newFilter);
              if (newFilter === 'custom') {
                setUseCustomDate(true);
              } else {
                setUseCustomDate(false);
              }
            }}
            variant="outlined"
            style={{ minWidth: 180 }}
          >
            {FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          {filter === 'custom' && (
            <React.Fragment>
              <TextField
                type="date"
                size="small"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                style={{ minWidth: 160 }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                style={{ minWidth: 160 }}
              />
            </React.Fragment>
          )}
          <BillingPicker
            label="Account"
            value={accountId}
            options={accountOptions}
            onChange={setAccountId}
          />
          <BillingPicker
            label="Location"
            value={locationId}
            options={locationOptions}
            onChange={setLocationId}
          />
          {displayDateRange && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 12px',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                marginLeft: 4,
              }}
            >
              {displayDateRange.start} – {displayDateRange.end}
            </Typography>
          )}
        </div>
      </div>

      {error && (
        <Typography className={classes.errorText} variant="body2">
          {error}
        </Typography>
      )}

      <div className={classes.tableWrapper} style={{ position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.5)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={32} />
          </div>
        )}
        <ILocalAGGrid
          columnDefs={columnDefs}
          defaultColDef={getDefaultColDef()}
          rowModelType="clientSide"
          rowData={invoices || []}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 25]}
          rowHeight={45}
          overlayNoRowsTemplate="No invoices found for the selected period."
          getRowId={(params) =>
            String(
              (params.data && (params.data.id || params.data.invoiceNumber)) ||
                `row-${Math.random().toString(36).slice(2)}`
            )
          }
          getContextMenuItems={getContextMenuItems}
        />
      </div>

      {selectedInvoice && (
        <InvoiceDetailDialog
          open={detailDialogOpen}
          invoice={selectedInvoice}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(InvoiceList);



// WEBPACK FOOTER //
// ./src/components/billing/InvoiceList.js